import React, { useCallback, useEffect, useRef, useState } from "react";
import useDesktopChecker from "hooks/useDesktopChecker";
import EsignGif from "assets/images/v2/Esign-gif.gif";
import EsignReview from "assets/images/v2/EsignReview.gif";
import EsignCompleted from "assets/images/v2/EsignCompleted.gif";
import { ReactComponent as Pen } from "assets/images/v2/Pen.svg";
import { ReactComponent as Iconography }  from "assets/images/v2/Iconography.svg";
import chevronLeft from "assets/images/v2/chevron-left.svg";
import { ESignStatus } from "../../dashboardService";
import { Button } from "components/v2/button/Button";
import Drawer, { DrawerType } from "components/ui/drawer/Drawer";
import ElectronicSignature from "pages/e-sign/ElectronicSignature";
import { 
  ESIGN_TITLE, 
  ESIGN_CLOSE_BUTTON_TEXT,
  ESIGN_FOOTER_TEXT,
  ESIGN_REDIRECTION_TOKEN 
} from "utilities/Constant";
import "./ESignPanel.scss";
import useTabletChecker from "hooks/useTabletChecker";

export interface ESignPanelProps {
  status?: string;
  loanAccessToken?: string;
  packageId?: string;
  readonly setShowUpload?: (show: boolean) => void;
  isEnabled?: boolean;
  onESignDrawerClosed?: () => void;
}

const ESignPanel: React.FC<ESignPanelProps> = ({
  status,
  loanAccessToken,
  packageId,
  setShowUpload,
  isEnabled,
  onESignDrawerClosed,
}) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const [isESignDrawerOpen, setIsESignDrawerOpen] = useState(false);
  /** Drawer opened via "E-Sign" (not "View"); parent refetch only applies to this path. */
  const openedViaESignButtonRef = useRef(false);
  /** Vendor script `onload` fired for this drawer session (interim gate until postMessage / completion). */
  const eccVendorScriptLoadedRef = useRef(false);

  const handleEccScriptLoaded = useCallback(() => {
    eccVendorScriptLoadedRef.current = true;
  }, []);

  const handleEccScriptError = useCallback(() => {
    eccVendorScriptLoadedRef.current = false;
  }, []);

  useEffect(() => {
    if (!isDesktop && !isTablet) {
      document.body.classList.add('esign-panel-visible');
    }
    return () => {
      document.body.classList.remove('esign-panel-visible');
    };
  }, [isDesktop, isTablet]);

  const handleESignClick = () => {
    if (loanAccessToken && packageId) {
      openedViaESignButtonRef.current = true;
      eccVendorScriptLoadedRef.current = false;
      localStorage.setItem(ESIGN_REDIRECTION_TOKEN, packageId);
      setIsESignDrawerOpen(true);
    }
  };

  const handleESignDrawerClose = () => {
    setIsESignDrawerOpen(false);
    const shouldRefetchNeeds =
      openedViaESignButtonRef.current && eccVendorScriptLoadedRef.current;
    openedViaESignButtonRef.current = false;
    eccVendorScriptLoadedRef.current = false;
    if (shouldRefetchNeeds) {
      onESignDrawerClosed?.();
    }
  };

  const handleViewClick = () => {
    if (loanAccessToken && packageId) {
      openedViaESignButtonRef.current = false;
      eccVendorScriptLoadedRef.current = false;
      localStorage.setItem(ESIGN_REDIRECTION_TOKEN, packageId);
      setIsESignDrawerOpen(true);
    }
  };

  return (
    <div className="esign-panel">
      {(!isDesktop && !isTablet) && (
        <button
          className="esign-panel__back-button"
          onClick={() => setShowUpload?.(false)}
          type="button"
        >
          <img src={chevronLeft} alt="Left arrow" />
          <p>Back to tasks</p>
        </button>
      )}

      {status?.toLowerCase() === ESignStatus.NEW && (
        <section className="esign-panel__section esign-panel__section--new">
          <img src={EsignGif} alt="Please E-Sign" />
          <span className="paragraph-medium">
            Please click E-Sign to review and sign your disclosure
          </span>
          <Button 
            label="E-Sign" 
            variant="primary" 
            icon={<Pen />} 
            iconPosition="left"
            disabled={!isEnabled}
            onClick={handleESignClick}
          />
        </section>
      )}

      {status?.toLowerCase() === ESignStatus.COMPLETED && (
        <section className="esign-panel__section esign-panel__section--completed">
          <img src={EsignCompleted} alt="Completed" />
          <span className="paragraph-medium">Click view to see your Disclosures</span>  
          <Button 
            label="View" 
            variant="primary" 
            icon={<Iconography />} 
            iconPosition="left"
            onClick={handleViewClick}
            disabled={!isEnabled}
          />
        </section>
      )}

      {status?.toLowerCase() === ESignStatus.UNDER_REVIEW && (
        <section className="esign-panel__section">
          <img src={EsignReview} alt="Review" />
          <div className="esign-panel__title">Under Review</div>
          <span className="esign-panel__description">
            If you e-signed all your documents we will mark this task as completed shortly. If you didn't finish, click E-sign again to complete this task.
          </span>
          <Button 
            label="E-Sign" 
            variant="primary" 
            icon={<Pen />} 
            iconPosition="left"
            disabled={!isEnabled}
            onClick={handleESignClick}
          />
        </section>
      )}

      {isESignDrawerOpen && (
        <Drawer
          isOpen={isESignDrawerOpen}
          onClose={handleESignDrawerClose}
          title={ESIGN_TITLE}
          closeText={ESIGN_CLOSE_BUTTON_TEXT}
          footerText={ESIGN_FOOTER_TEXT}
          type={DrawerType.ElectronicSignature}
          centerContent
        >
          <>
            <ElectronicSignature
              loanAccessToken={loanAccessToken}
              packageId={packageId}
              onScriptLoad={handleEccScriptLoaded}
              onScriptError={handleEccScriptError}
            />
          </>
        </Drawer>
      )}
    </div>
  );
};

export default ESignPanel;